import { useMemo } from 'react';

// context
import { AppContextProvider } from '@/components/utilities/AppContext';

// components
import Page404 from './Page404';
import Page500 from './Page500';
import PageHead from './PageHead';
import ComponentsRender from '@/components/ComponentsRender';
import ScrollSensor from '@/components/utilities/ScrollSensor';
import GlossaryDetailPage from '@/components/components/glossary/GlossaryDetailPage';
import EbookDetailPage from '@/components/components/ebook/EbookDetailPage';

// constants
import { LAYOUT_BLOCKS } from '@/components/components';

// utils
import { buildFaqPageSchema, buildArticleSchema } from '@/utils/json-ld-schemas';
import { getGlossaryRootForLocale } from '@/utils/glossary-routes';
import { getGlossaryLabels } from '@/utils/glossary-labels';
import { getEbookRootForLocale } from '@/utils/ebook-routes';
import { collectFaqItems } from '@/utils/faq-items';
import { resolveTutorialsDeepPath } from '@/utils/tutorials-routes';

const NAV_TYPENAMES = new Set(['ComponentLayoutBlocksNavigationBlock']);
const FOOTER_TYPENAMES = new Set(['ComponentLayoutBlocksFooterBlock']);
const QUESTIONS_BLOCK_TYPENAME = 'ComponentComponentsQuestionsBlock';
const GLOSSARY_FAQ_TYPENAME = 'ComponentGlossaryFaq';
const EBOOK_FAQ_TYPENAME = 'ComponentEbookFaq';

const Page = ({
  pageData,
  error,
  locale,
  rootPath,
  restOfPath,
  queryParams,
  isPreview,
  glossaryTerm = null,
  glossaryTerms = null,
  glossarySettings = null,
  ebook = null
}) => {
  const appContext = useMemo(
    () => ({
      locale,
      rootPath,
      restOfPath,
      queryParams,
      glossaryTerm,
      glossaryTerms,
      glossarySettings,
      ebook
    }),
    [locale, restOfPath, rootPath, queryParams, glossaryTerm, glossaryTerms, glossarySettings, ebook]
  );

  if (error) {
    return <Page500 error={error} />;
  }

  if (!pageData) {
    return <Page404 />;
  }

  const { title, description, path, localizations, seoTitle, seoDescription, ogImage, noIndex } = pageData;
  const content = pageData.content || [];

  const isGlossaryDetail = Boolean(glossaryTerm);
  const isEbookDetail = Boolean(ebook);

  const localePrefix = locale === 'cs' ? '' : `/${locale}`;

  // Prefer SEO-specific fields from Strapi; fall back to the regular title/description.
  // Marketing can write keyword-optimised metadata without touching the page H1/intro.
  // PageHead always adds Organization, WebSite, WebPage, and BreadcrumbList; here we
  // compute per-mode head metadata and the extra FAQPage/Article JSON-LD schemas.
  let headTitle;
  let headDescription;
  let headOgImage;
  let headNoIndex;
  let headPath;
  let headBreadcrumbs = null;
  let headLocalizations;
  let faqQuestions;
  let articleSchema = null;

  if (isGlossaryDetail) {
    headTitle = glossaryTerm.seoTitle || glossaryTerm.title;
    headDescription = glossaryTerm.seoDescription || glossaryTerm.shortDescription;
    headOgImage = glossaryTerm.ogImage;
    headNoIndex = glossaryTerm.noIndex;

    const glossaryRootPath = getGlossaryRootForLocale(locale);
    headPath = `${glossaryRootPath}/${glossaryTerm.slug}`;
    const glossaryRootUrl = `${process.env.NEXT_PUBLIC_SITE_URL}${localePrefix}${glossaryRootPath}`;
    const glossaryTermUrl = `${glossaryRootUrl}/${glossaryTerm.slug}`;

    const glossaryLabels = getGlossaryLabels(glossarySettings, locale);
    headBreadcrumbs = [
      { name: glossaryLabels.rootLabel, url: glossaryRootUrl },
      { name: glossaryTerm.title, url: glossaryTermUrl }
    ];

    // Glossary localizations have {locale, slug} shape; remap to {locale, path}
    // so PageHead's hreflang generator can use them uniformly.
    headLocalizations = (glossaryTerm.localizations || []).map((loc) => ({
      locale: loc.locale,
      path: `${getGlossaryRootForLocale(loc.locale)}/${loc.slug}`
    }));

    faqQuestions = collectFaqItems(glossaryTerm.content, GLOSSARY_FAQ_TYPENAME, 'items');
    articleSchema = buildArticleSchema({
      title: glossaryTerm.title,
      description: glossaryTerm.shortDescription,
      canonicalUrl: glossaryTermUrl,
      locale,
      publishedAt: glossaryTerm.publishedAt,
      updatedAt: glossaryTerm.updatedAt
    });
  } else if (isEbookDetail) {
    headTitle = ebook.seoTitle || ebook.title;
    headDescription = ebook.seoDescription || ebook.shortDescription;
    headOgImage = ebook.ogImage;
    headNoIndex = ebook.noIndex;

    const ebookRootPath = getEbookRootForLocale(locale);
    headPath = `${ebookRootPath}/${ebook.slug}`;
    const ebookUrl = `${process.env.NEXT_PUBLIC_SITE_URL}${localePrefix}${ebookRootPath}/${ebook.slug}`;

    // No ebook overview page, so the breadcrumb has a single entry: the ebook itself.
    headBreadcrumbs = [{ name: ebook.title, url: ebookUrl }];

    headLocalizations = (ebook.localizations || []).map((loc) => ({
      locale: loc.locale,
      path: `${getEbookRootForLocale(loc.locale)}/${loc.slug}`
    }));

    faqQuestions = collectFaqItems(ebook.content, EBOOK_FAQ_TYPENAME, 'items');
    articleSchema = buildArticleSchema({
      title: ebook.title,
      description: ebook.shortDescription,
      canonicalUrl: ebookUrl,
      locale,
      publishedAt: ebook.publishedAt,
      updatedAt: ebook.updatedAt
    });
  } else {
    headTitle = seoTitle || title;
    headDescription = seoDescription || description;
    headOgImage = ogImage;
    headNoIndex = noIndex;
    headPath = `${path}${resolveTutorialsDeepPath(restOfPath, content)}`;
    headLocalizations = localizations;
    faqQuestions = collectFaqItems(content, QUESTIONS_BLOCK_TYPENAME, 'questions');
  }

  const extraSchemas = [buildFaqPageSchema(faqQuestions), articleSchema].filter(Boolean);

  return (
    <>
      <PageHead
        title={headTitle}
        description={headDescription}
        path={headPath}
        locale={locale}
        localizations={headLocalizations}
        isPreview={isPreview}
        ogImage={headOgImage}
        noIndex={headNoIndex}
        breadcrumbs={headBreadcrumbs}
        extraSchemas={extraSchemas}
      />

      <AppContextProvider value={appContext}>
        <ScrollSensor>
          {isGlossaryDetail || isEbookDetail ? (
            <>
              {content
                .filter((component) => NAV_TYPENAMES.has(component.__typename))
                .map((component) => (
                  <ComponentsRender
                    key={`${component.id}_${component.__typename}`}
                    type={LAYOUT_BLOCKS}
                    component={component}
                  />
                ))}

              {isGlossaryDetail ? <GlossaryDetailPage /> : <EbookDetailPage />}

              {content
                .filter((component) => FOOTER_TYPENAMES.has(component.__typename))
                .map((component) => (
                  <ComponentsRender
                    key={`${component.id}_${component.__typename}`}
                    type={LAYOUT_BLOCKS}
                    component={component}
                  />
                ))}
            </>
          ) : (
            content.map((component) => (
              <ComponentsRender
                key={`${component.id}_${component.__typename}`}
                type={LAYOUT_BLOCKS}
                component={component}
              />
            ))
          )}
        </ScrollSensor>
      </AppContextProvider>
    </>
  );
};
export default Page;
